import { isEmpty, isNil } from 'lodash';

import type { ChannelTabType, Communication, CommunicationChannelKey } from '~/components/communications/types';
import type { ClaimPolymorph } from '~/components/types/claim-types';
import useIdBasedPagination from '~/hooks/useIdBasedPagination';
import type {
  ClaimCommunicationItemIdsParams,
  IdsResponseIntCommunicationIdsExtraDataCommunicationIdsExtraData as CommunicationIdsExtraData,
} from '~/server_shared/openapi/data-contracts';

const DEFAULT_ROWS_PER_PAGE = 10;

interface useCommunicationsPaginationProps {
  claimId?: ClaimPolymorph['id'];
  organizationId?: number;
  initialRowsPerPage?: number;
  sortByColumn?: string;

  exposuresFilters?: number[] | null;
  contactId?: number | null;
  userId?: string | null;
  communicationChannel?: CommunicationChannelKey | null;
  communicationChannels?: CommunicationChannelKey[] | null;
  categoriesIds?: number[] | null;
  searchText?: string;
  handledStatus?: ClaimCommunicationItemIdsParams['handled_status'];
}

export const useCommunicationsPagination = ({
  claimId,
  organizationId,
  initialRowsPerPage = DEFAULT_ROWS_PER_PAGE,
  sortByColumn = '-datetime',
  exposuresFilters,
  contactId,
  userId,
  communicationChannel,
  communicationChannels,
  categoriesIds,
  searchText,
  handledStatus,
}: useCommunicationsPaginationProps) => {
  const isClaimScoped = !!claimId;

  const FETCH_IDS_URL = isClaimScoped
    ? `/api/v1/claims/${claimId}/communications/communications_ids`
    : `/api/v1/communications/${organizationId}/users/${userId}/communications/communications_ids`;

  const FETCH_PAGE_DATA_URL = isClaimScoped
    ? `/api/v1/claims/${claimId}/communications/communications_by_ids`
    : `/api/v1/communications/${organizationId}/users/${userId}/communications/communications_by_ids`;

  const buildParams = (options: {
    sortBy?: string;
    exposuresFilters?: number[] | null;
    contactId?: number | null;
    userId?: string | null;
    communicationChannel?: CommunicationChannelKey | null;
    communicationChannels?: CommunicationChannelKey[] | null;
    categoriesIds?: number[] | null;
    searchText?: string;
    handledStatus?: ClaimCommunicationItemIdsParams['handled_status'];
  }): Partial<ClaimCommunicationItemIdsParams> => {
    const params: Partial<ClaimCommunicationItemIdsParams> = {};

    if (options.sortBy !== undefined) {
      params.sort_by = options.sortBy;
    }

    if (isClaimScoped) {
      if (!isEmpty(options.exposuresFilters)) {
        params.exposures_filters = options.exposuresFilters;
      }

      if (!isEmpty(options.userId)) {
        params.user_id = options.userId;
      }
    }

    // Both scopes accept the handled-status filter; only the filters above are claim-only.
    if (!isNil(options.handledStatus)) {
      params.handled_status = options.handledStatus;
    }

    if (!isNil(options.contactId)) {
      params.contact_id = options.contactId;
    }

    if (!isEmpty(options.communicationChannel)) {
      params.communication_channel = options.communicationChannel;
    }

    if (!isEmpty(options.communicationChannels)) {
      params.communication_channels = options.communicationChannels;
    }

    if (!isEmpty(options.categoriesIds)) {
      params.categories_ids = options.categoriesIds;
    }

    params.with_read_status = true;

    if (!isNil(options.searchText) && options.searchText.trim() !== '') {
      params.search_text = options.searchText;
    }

    return params;
  };

  const fetchIdsParams = buildParams({
    sortBy: sortByColumn,
    exposuresFilters,
    contactId,
    userId,
    communicationChannel,
    communicationChannels,
    categoriesIds,
    searchText,
    handledStatus,
  });

  const fetchPageDataParams = buildParams({});

  return useIdBasedPagination<
    number,
    CommunicationIdsExtraData,
    Communication,
    {
      available_channels?: ChannelTabType[];
      communications_adjusters?: Array<{ id: string; username: string }>;
      communications_read_status?: Record<string, boolean>;
    }
  >({
    fetchIdsUrl: FETCH_IDS_URL,
    fetchPageDataUrl: FETCH_PAGE_DATA_URL,
    fetchIdsParams,
    fetchPageDataParams,
    initialRowsPerPage,
  });
};
